// Shipping (spec §3.13–3.14, §5): the Shipping schedule and the Ship log, as pure functions of the
// lines `assemble` makes. The checks on a pickup and a shipment (the big red message: blocked) and
// the warnings ("Are you sure?": may go on); In / Out, CTNS / PLTS; the schedule's groups by
// pickup date and the Ship log's by ship date. Nothing here is from A2000 but the line's own
// values: pickups are typed by the team.

import { isDay, shiftDayMonths } from "@axny/platform-contracts";
import { a2000Text } from "./a2000";
import type { LineView } from "./assemble";
import { lineRef } from "./assemble";
import { customerSearchText } from "./customer-names";
import { pushDateRule } from "./date-rules";
import { addDays, dayOfWeek, formatShortDay } from "./days";
import { parseDateInput } from "./dates";
import { momentOf } from "./pe-order";
import { halfOf, hour12, hour24, timeOf, timeShown } from "./time";
import type { TimeHalf } from "./time";

export { TIME_HALVES, clockText, halfOf, hour12, hour24, timeOf, timeShown } from "./time";
export type { TimeHalf } from "./time";
import { ptText, ptWord } from "./pt-text";
import type { PickupRecord, ShipmentRecord, ShippingRowRecord } from "./types";

/** Routed enough to ship: the window's "Routed" — a status in the Routed category (Routed, Pre-routed…). */
export const isRouted = (l: Pick<LineView, "routingCategory">) => l.routingCategory === "Routed";

/** "That date looks wrong": more than a year away or before 2025 (spec §5). */
export const dateLooksWrong = (day: string, today: string) =>
  day < "2025-01-01" || day > shiftDayMonths(today, 12) || day < shiftDayMonths(today, -12);

const WRONG_DATE = "That date looks wrong — check the year (it is more than a year away or before 2025)";

/**
 * A day and a time (PE Order Start Time, End Time) as stored, "2026-10-30 14:05"; "" for nothing.
 * The day as a date cell reads it ("10/30", "10/30/26", "2026-10-30"; no year, the nearest), the time
 * as In: and Out: do ("14:05", "2:05 pm"): what the picker and Now fill, what the cell shows, typed.
 */
export function parseMoment(text: string, today: string): { ok: true; value: string } | { ok: false; message: string } {
  const t = text.trim();
  if (!t) return { ok: true, value: "" };
  const bad = { ok: false as const, message: "Use a date and a time like 10/30 14:05" };
  const [d = "", ...rest] = t.split(/\s+/);
  const day = parseDateInput(d, "routedOn", today);
  const time = parseTime(rest.join(" "));
  if (!day.ok || !day.value || !time.ok || !time.value) return bad;
  return { ok: true, value: momentOf(day.value, time.value) };
}

/** A typed time, "7:30", "07:30", "14:00", "730", "2:15 pm", as H:MM (24 h); "" for nothing. */
export function parseTime(text: string): { ok: true; value: string } | { ok: false; message: string } {
  const t = text.trim().toLowerCase();
  if (!t) return { ok: true, value: "" };
  const m = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/.exec(t);
  const bad = { ok: false as const, message: "Use a time like 7:30 AM or 2:30 PM" };
  if (!m) return bad;
  let h = Number(m[1]);
  const min = Number(m[2] ?? "0");
  if (m[3]?.startsWith("p") && h < 12) h += 12;
  if (m[3]?.startsWith("a") && h === 12) h = 0;
  if (h > 23 || min > 59 || (m[3] && Number(m[1]) > 12)) return bad;
  return { ok: true, value: `${h}:${String(min).padStart(2, "0")}` };
}

/**
 * A typed time as its box shows it once left (blur): what will be stored, as every page shows it
 * ("14:30" → "2:30 PM"); empty or not a time, as typed — the save says why.
 */
export function typedTimeShown(text: string): string {
  const t = parseTime(text);
  return t.ok && t.value ? timeShown(t.value) : text;
}

/** The time picker's lists: hours 12, 1–11 (beside AM / PM), minutes 00–55 in 5-minute steps (a typed time keeps its exact minute). */
export const TIME_HOURS: readonly number[] = [12, ...Array.from({ length: 11 }, (_, i) => i + 1)];
export const TIME_MINUTES: readonly number[] = Array.from({ length: 12 }, (_, i) => i * 5);

/** A moment's local time to the minute, as stored (H:MM): the picker's Now. */
export const timeAt = (d: Date) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;

/** A typed or stored time's hour and minute, for the picker's highlight; null when it is no time. */
export function timeParts(text: string): { h: number; m: number } | null {
  const t = parseTime(text);
  if (!t.ok || !t.value) return null;
  const [h, m] = t.value.split(":").map(Number);
  return { h: h ?? 0, m: m ?? 0 };
}

/** A pick in the time picker: an hour (12, 1–11), a minute, AM / PM, Now or Clear. */
export type TimePick = { hour: number } | { minute: number } | { half: TimeHalf } | "now" | "clear";

/**
 * The AM / PM switch before anything is picked: the time's own half, else the half of the day it is
 * now (AM before 12:00, PM from 12:00).
 */
export const halfShown = (text: string, now: Date): TimeHalf => {
  const parts = timeParts(text);
  return halfOf(parts ? parts.h : now.getHours());
};

/**
 * The editor's text after a pick, and whether it saves: an hour (in the switch's half, `half`) keeps
 * the minute (00 when there is none) and waits; AM / PM turns the time shown to that half and waits;
 * a minute (on the hour shown, else `now`'s), Now (the current half) and Clear save. The text is what
 * is saved, so a refused one stays in the editor — its hour, minute and half still picked.
 */
export function timePick(
  text: string,
  pick: TimePick,
  now: Date,
  half: TimeHalf = halfShown(text, now),
): { text: string; save: boolean } {
  const parts = timeParts(text);
  if (pick === "clear") return { text: "", save: true };
  if (pick === "now") return { text: timeShown(timeAt(now)), save: true };
  if ("hour" in pick) return { text: timeOf(hour24(pick.hour, half), parts?.m ?? 0), save: false };
  if ("half" in pick) return { text: parts ? timeOf(hour24(hour12(parts.h), pick.half), parts.m) : text, save: false };
  return { text: timeOf(parts?.h ?? now.getHours(), pick.minute), save: true };
}

const minutes = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Out can't be before In (spec §5); undefined when they may be saved. */
export function checkInOut(inTime: string, outTime: string): string | undefined {
  if (inTime && outTime && minutes(outTime) < minutes(inTime))
    return `Out (${timeShown(outTime)}) can't be before In (${timeShown(inTime)})`;
  return undefined;
}

/** CTNS and PLTS: whole numbers, 0 or more; "" clears. */
export function parseWhole(
  text: string,
  label: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const t = text.trim().replace(/,/g, "");
  if (!t) return { ok: true, value: null };
  if (!/^\d{1,6}$/.test(t)) return { ok: false, message: `${label} is a whole number, like 12` };
  return { ok: true, value: Number(t) };
}

/**
 * The kind of a reference number: its letters before the digits ("RTS# 2403991" → RTS, "LD-48211"
 * → LD); null when it has none (a bare number says nothing of its kind).
 */
export function refKind(ref: string): string | null {
  const m = /^\s*([A-Za-z]+)[\s#:._-]*\d/.exec(ref);
  return m ? m[1]!.toUpperCase() : null;
}
const refDigits = (ref: string) => ref.replace(/[^0-9A-Za-z]/g, "").toUpperCase();

/**
 * The routing reference and the PICK UP # are the same kind of number (both RTS#…) but differ
 * (spec §5: a warning; Isaac and Meir to confirm, §7 question 9).
 */
export const refsDisagree = (routingRef: string, loadRef: string) => {
  const kind = refKind(routingRef);
  return kind !== null && kind === refKind(loadRef) && refDigits(routingRef) !== refDigits(loadRef);
};

/** What a check says: blocked (`errors`, the big red message) or to confirm (`warnings`). */
export interface Checked {
  errors: string[];
  warnings: string[];
}

/** What names a line in a message: its customer, and its WMS # or Deposco PO (`lineRef`). */
type Named = Pick<LineView, "customerName" | "wmsNo" | "source" | "pos" | "dcs">;
const lineName = (l: Named) => `${l.customerName || "A line"} (${lineRef(l) || "no WMS #"})`;

/**
 * A pickup for these lines (spec §5): blocked — no date or a wrong one; the PICK UP # is optional
 * (item 8). To confirm —
 * a date in the past, after a line's cancel date, before its ready date or first ship date
 * (`DATE_RULES`); a line not routed yet; a routing reference of the same kind as the PICK UP # but
 * different.
 */
export function checkPickup(
  pickup: { pickupDate: string; loadRef: string },
  lines: readonly (Named & Pick<LineView, "cancel" | "start" | "fields" | "routingCategory">)[],
  today: string,
): Checked {
  const checked: Checked = { errors: [], warnings: [] };
  const { errors, warnings } = checked;
  const d = pickup.pickupDate;
  const valid = isDay(d) && !dateLooksWrong(d, today);
  if (!isDay(d)) errors.push("The pickup needs a date, like 10/16");
  else if (!valid) pushDateRule(checked, "wrongYear", WRONG_DATE);
  else if (d < today) pushDateRule(checked, "pickupInPast", "The pickup date is in the past");
  for (const l of lines) {
    if (valid && l.cancel && d > l.cancel)
      pushDateRule(
        checked,
        "pickupAfterCancel",
        `${lineName(l)}: the pickup is after its cancel date (${formatShortDay(l.cancel)})`,
      );
  }
  for (const l of lines) {
    if (!isRouted(l)) warnings.push(`${lineName(l)} is not routed yet (${l.fields.routingStatus})`);
    if (valid && isDay(l.fields.readyDate) && d < l.fields.readyDate)
      pushDateRule(
        checked,
        "pickupBeforeReady",
        `${lineName(l)}: the pickup is before its READY DATE (${formatShortDay(l.fields.readyDate)})`,
      );
    if (valid && l.start && d < l.start)
      pushDateRule(
        checked,
        "pickupBeforeStart",
        `${lineName(l)}: the pickup is before its FIRST SHIP DATE (${formatShortDay(l.start)})`,
      );
    // Only when both are filled: a blank PICK UP # says nothing against a Routing Reference.
    if (pickup.loadRef.trim() && l.fields.routingRef.trim() && refsDisagree(l.fields.routingRef, pickup.loadRef))
      warnings.push(
        `${lineName(l)}: its Routing Reference ${l.fields.routingRef} is not the PICK UP # ${pickup.loadRef.trim()}`,
      );
  }
  return { errors, warnings };
}

/**
 * Marking lines shipped (spec §3.7, §5): blocked — no ship date, a wrong one or one in the future; the
 * PICK UP # / BOL is optional (item 8). To confirm — a ship date after a line's cancel date (`DATE_RULES`), a line not routed.
 */
export function checkShipment(
  s: { shipDate: string; loadRef: string },
  lines: readonly (Named & Pick<LineView, "cancel" | "fields" | "routingCategory">)[],
  today: string,
): Checked {
  const checked: Checked = { errors: [], warnings: [] };
  const d = s.shipDate;
  const valid = isDay(d) && !dateLooksWrong(d, today);
  if (!isDay(d)) checked.errors.push("The shipment needs its ship date, like 10/13");
  else if (!valid) pushDateRule(checked, "wrongYear", WRONG_DATE);
  else if (d > today) pushDateRule(checked, "shipDateInFuture", "The ship date can't be in the future");
  for (const l of lines) {
    if (valid && l.cancel && d > l.cancel)
      pushDateRule(
        checked,
        "shipDateAfterCancel",
        `${lineName(l)}: the ship date is after its cancel date (${formatShortDay(l.cancel)})`,
      );
    if (!isRouted(l)) checked.warnings.push(`${lineName(l)} is not routed yet (${l.fields.routingStatus})`);
  }
  return checked;
}

/**
 * An edit of a scheduled pickup's Date or PICK UP # (the schedule's light-teal cells): only what
 * the edit itself causes. A new date is checked as a pickup date: in the past, or a line it puts
 * after its cancel date or before its READY DATE or FIRST SHIP DATE, is an "Are you sure?"; a new PICK UP # may be blank (item 8) and warns where a
 * routing reference of the same kind differs. "Not routed" was confirmed when the lines were scheduled:
 * not asked again.
 */
export function checkPickupEdit(
  field: "pickupDate" | "loadRef",
  pickup: { pickupDate: string; loadRef: string },
  lines: readonly (Named & Pick<LineView, "cancel" | "start" | "fields" | "routingCategory">)[],
  today: string,
): Checked {
  const all = checkPickup(pickup, lines, today);
  const ofDate = (m: string) => / pickup (date|is after|is before|needs a date)|date looks wrong/.test(m);
  const ofRef = (m: string) => /PICK UP #|Routing Reference/.test(m);
  return field === "pickupDate"
    ? { errors: all.errors.filter(ofDate), warnings: all.warnings.filter(ofDate) }
    : { errors: all.errors.filter(ofRef), warnings: all.warnings.filter(ofRef) };
}

/** What Add a line refuses, in the big red message's words. */
export interface AddLineRefusal {
  title: string;
  body: string;
  lines?: string[];
}

/**
 * Add a line (spec §3.13): from the lines the typed PO, PT or SO # is on — open ones (`findOpenLines`)
 * and finished ones (`findFinishedLines`) — the one line to add, or why not: shipped or cancelled,
 * on no open line, on several, or already on a pickup. Then the pickup it goes on (the scheduled
 * one of that date and PICK UP #, else a new one — a blank PICK UP # never matches one by itself) and its
 * checks, about that line only.
 */
export function planAddLine(input: {
  typed: string;
  open: readonly LineView[];
  finished: readonly LineView[];
  pickups: readonly PickupRecord[];
  pickupDate: string;
  loadRef: string;
  today: string;
}): { refuse: AddLineRefusal } | { line: LineView; pickup: PickupRecord | null; checked: Checked } {
  const typed = input.typed.trim();
  const [done] = input.finished;
  if (done?.finished && !input.open.length) {
    const { status, by } = done.finished;
    return {
      refuse: {
        title: finishedText(done),
        body: `“${typed}” is on ${lineName(done)}, ${status.toLowerCase()} in the app${by ? ` by ${by}` : ""}. It can't go on the schedule again.`,
      },
    };
  }
  const [line, ...more] = input.open;
  if (!line)
    return {
      refuse: {
        title: "That line is not open on the Worklist",
        body: `No open Worklist line has the PO, PT or SO # “${typed || "…"}”. Only open lines with a WMS # or a Deposco line can go on the schedule.`,
      },
    };
  if (more.length)
    return {
      refuse: {
        title: "That number is on several lines",
        body: `“${typed}” is on ${input.open.length} open lines. Type the PT # (SO #) of the one to add.`,
        lines: input.open.map((l) => `${l.customerName} · ${lineRef(l)} · ${ptWord(l)} ${ptText(l)}`),
      },
    };
  if (line.onPickup) {
    const on = line.onPickup.pickup;
    const where = [on.pickupDate ? formatShortDay(on.pickupDate) : "", on.carrier, on.loadRef]
      .filter(Boolean)
      .join(" · ");
    return {
      refuse: {
        title: "Already on a pickup",
        body: `${lineName(line)} is already on the pickup ${where}${on.appTime ? ` at ${timeShown(on.appTime)}` : ""}. A line is on one pickup at a time: take it off that pickup first, or ship it with it.`,
      },
    };
  }
  const ref = input.loadRef.trim().toUpperCase();
  const pickup =
    (ref &&
      input.pickups.find(
        (p) => p.status === "Scheduled" && p.pickupDate === input.pickupDate && p.loadRef.trim().toUpperCase() === ref,
      )) ||
    null;
  // The line being added, and only it: the pickup's other lines were checked when they went on it.
  const checked = checkPickup(
    { pickupDate: input.pickupDate, loadRef: pickup?.loadRef ?? input.loadRef },
    [line],
    input.today,
  );
  return { line, pickup, checked };
}

/**
 * The pickups "Add to an existing pickup" offers: Scheduled, from today on, with a line on them,
 * soonest first. A pickup left with no line — its save cut short after "New pickup" — is not
 * offered (the schedule, built from its rows, does not show it either).
 */
export function openPickups(
  pickups: readonly PickupRecord[],
  rowsOn: (pickupId: string) => number,
  today: string,
): { pickup: PickupRecord; rows: number }[] {
  return pickups
    .filter((p) => p.status === "Scheduled" && (p.pickupDate ?? "") >= today)
    .map((pickup) => ({ pickup, rows: rowsOn(pickup.id) }))
    .filter((p) => p.rows > 0)
    .sort(
      (a, b) =>
        (a.pickup.pickupDate ?? "").localeCompare(b.pickup.pickupDate ?? "") ||
        a.pickup.appTime.localeCompare(b.pickup.appTime),
    );
}

/** A row's warning icon (spec §3.13): what to check, in a few words each. */
export function rowWarnings(
  line: Pick<LineView, "a2000" | "fields" | "cancel" | "routingCategory">,
  pickup: Pick<PickupRecord, "pickupDate">,
): string[] {
  const d = pickup.pickupDate;
  return [
    ...(line.a2000 ? [a2000Text(line.a2000)] : []),
    ...(isRouted(line) ? [] : ["not routed"]),
    ...(d && line.cancel && d > line.cancel ? [`pickup after cancel (${formatShortDay(line.cancel)})`] : []),
    ...(d && isDay(line.fields.readyDate) && line.fields.readyDate > d
      ? [`ready after pickup (${formatShortDay(line.fields.readyDate)})`]
      : []),
  ];
}

/** One row of the Shipping schedule: a line on a scheduled pickup. */
export interface ScheduleRow {
  line: LineView;
  row: ShippingRowRecord;
  pickup: PickupRecord;
}

export const scheduleRows = (lines: readonly LineView[]): ScheduleRow[] =>
  lines.flatMap((line) => (line.onPickup ? [{ line, ...line.onPickup }] : []));

/** "Today · Tue 10/13", "Tomorrow · Wed 10/14", "Fri 10/16". */
export function dayLabel(day: string, today: string): string {
  const d = `${dayOfWeek(day)} ${formatShortDay(day)}`;
  if (day === today) return `Today · ${d}`;
  if (day === addDays(today, 1)) return `Tomorrow · ${d}`;
  return d;
}

/** "1 line", "2 lines", "1,296 units": a count with its word, singular for one. */
export const countText = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const plural = countText;

export interface ScheduleGroup {
  day: string;
  label: string;
  /** Before today and still not shipped: shown first, in red. */
  late: boolean;
  /** "2 lines · 1 pickup" */
  counts: string;
  rows: ScheduleRow[];
}

/**
 * The schedule by pickup date: open past dates first (late), then today and on; in a day by App
 * Time, pickup, then the row's order.
 */
export function scheduleGroups(rows: readonly ScheduleRow[], today: string): ScheduleGroup[] {
  const byDay = new Map<string, ScheduleRow[]>();
  for (const r of rows) {
    const day = r.pickup.pickupDate ?? "";
    byDay.set(day, [...(byDay.get(day) ?? []), r]);
  }
  // App Time as typed; one that is not a time sorts last.
  const time = (t: string) => {
    const p = parseTime(t);
    return p.ok && p.value ? String(minutes(p.value)).padStart(4, "0") : "9999";
  };
  return [...byDay]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, rs]) => {
      const sorted = [...rs].sort(
        (a, b) =>
          time(a.pickup.appTime).localeCompare(time(b.pickup.appTime)) ||
          a.pickup.id.localeCompare(b.pickup.id, undefined, { numeric: true }) ||
          (a.row.sortOrder ?? 0) - (b.row.sortOrder ?? 0) ||
          a.row.id.localeCompare(b.row.id, undefined, { numeric: true }),
      );
      const pickups = new Set(rs.map((r) => r.pickup.id)).size;
      return {
        day,
        label: day ? dayLabel(day, today) : "No date",
        late: !!day && day < today,
        counts: `${plural(rs.length, "line")} · ${plural(pickups, "pickup")}`,
        rows: sorted,
      };
    });
}

/** One row of the Ship log: a line the app marked shipped. */
export interface ShipLogRow {
  line: LineView;
  shipment: ShipmentRecord;
}

export const shipLogRows = (lines: readonly LineView[]): ShipLogRow[] =>
  lines.flatMap((line) =>
    line.finished?.status === "Shipped" && line.shipment ? [{ line, shipment: line.shipment }] : [],
  );

export interface ShipLogGroup {
  day: string;
  /** "Sat 10/10 · 1 line · 1 load · 1,296 units" */
  label: string;
  rows: ShipLogRow[];
}

/**
 * The Ship log by ship date, newest first; inside a day the shipment marked last first. A load is a
 * pickup, or a line shipped on its own.
 */
export function shipLogGroups(rows: readonly ShipLogRow[]): ShipLogGroup[] {
  const byDay = new Map<string, ShipLogRow[]>();
  for (const r of rows) {
    const day = r.shipment.shipDate ?? "";
    byDay.set(day, [...(byDay.get(day) ?? []), r]);
  }
  return [...byDay]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, rs]) => {
      const loads = new Set(rs.map((r) => r.shipment.pickupId || r.shipment.id)).size;
      const units = rs.reduce((n, r) => n + r.line.qty, 0);
      const head = day ? `${dayOfWeek(day)} ${formatShortDay(day)}` : "No date";
      return {
        day,
        label: `${head} · ${plural(rs.length, "line")} · ${plural(loads, "load")} · ${plural(units, "unit")}`,
        // Inside a day, the shipment marked last first (its record's created_at; the id breaks a tie).
        rows: [...rs].sort(
          (a, b) =>
            String(b.shipment.created_at ?? "").localeCompare(String(a.shipment.created_at ?? "")) ||
            b.shipment.id.localeCompare(a.shipment.id, undefined, { numeric: true }),
        ),
      };
    });
}

/**
 * The open Worklist lines a typed PO, PT or SO # names (Add a line at the bottom): exact, on an
 * open line in the WMS. Several only when the number is on several lines.
 */
export function findOpenLines(lines: readonly LineView[], typed: string): LineView[] {
  return lines.filter((l) => !l.finished && !l.noWms && namesLine(l, typed, true));
}

/** The finished lines (shipped or cancelled in the app) a typed PO, PT or SO # names: refused, with when. */
export function findFinishedLines(lines: readonly LineView[], typed: string): LineView[] {
  return lines.filter((l) => !!l.finished && l.finished.status !== "Regrouped by WMS" && namesLine(l, typed, false));
}

/** A typed PO, order # or PT # is the line's, exactly (any case); `open`: its open pick tickets only. */
function namesLine(l: LineView, typed: string, open: boolean): boolean {
  const t = typed.trim().toUpperCase();
  if (!t) return false;
  return (
    l.pos.some((p) => p.toUpperCase() === t) ||
    l.orderNos.some((o) => o.toUpperCase() === t) ||
    l.pts.some((p) => (!open || !p.gone) && p.pickTicket === t)
  );
}

/** Why Add a line refuses a finished line: "Already shipped on 10/21 — see the Ship log". */
export function finishedText(l: Pick<LineView, "finished">): string {
  const f = l.finished;
  if (!f) return "";
  const on = f.at ? ` on ${formatShortDay(f.at)}` : "";
  return f.status === "Shipped" ? `Already shipped${on} — see the Ship log` : `Cancelled${on} — see History`;
}

/** What the top bar's search finds on the Shipping pages: customer, PO, PT / SO, load #, carrier, tracking. */
export function shippingMatches(
  line: LineView,
  q: string,
  also: { loadRef?: string; carrier?: string; tracking?: string; wmsAsnTrip?: string },
): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [
    // A customer by its shown name, its original name or its code.
    customerSearchText(line),
    // A division by its full name or its abbreviation.
    line.divisions.join(" "),
    line.divs.map((d) => d.abbrev).join(" "),
    line.pos.join(" "),
    line.orderNos.join(" "),
    line.pts.map((p) => p.pickTicket).join(" "),
    line.wmsNo,
    // A Deposco line by its DLIX, CO, group or DC.
    line.dcs.join(" "),
    ...(line.deposco ? [line.deposco.dlixes, line.deposco.cos, line.deposco.groups].map((v) => v.join(" ")) : []),
    also.loadRef ?? "",
    also.carrier ?? "",
    also.tracking ?? "",
    also.wmsAsnTrip ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(s);
}

/** "10/16 · FedEx Freight · LD# 50001": a pickup in a few words. */
export const pickupText = (p: Partial<Pick<PickupRecord, "pickupDate" | "carrier" | "loadRef">>) =>
  [p.pickupDate ? formatShortDay(p.pickupDate) : "", p.carrier, p.loadRef].filter(Boolean).join(" · ");

/** A line's System log note when it goes on a pickup: "Scheduled pickup 11/05 · UPS GROUND · QA-R2 · 2:30 PM · Door 4",
 * the App Time as the pages show it (`timeShown`), never as stored. */
export const scheduledNote = (
  p: Partial<Pick<PickupRecord, "pickupDate" | "carrier" | "loadRef" | "appTime" | "door">>,
  movedFrom?: Partial<Pick<PickupRecord, "pickupDate" | "carrier" | "loadRef">>,
) =>
  `Scheduled pickup ${pickupText(p)}${p.appTime ? ` · ${timeShown(p.appTime)}` : ""}` +
  (movedFrom ? ` (moved from the pickup ${pickupText(movedFrom)})` : p.door ? ` · Door ${p.door}` : "");

/** A line as a done / to-do list says it: "TJ MAXX · WMS 37101", "BELK · PO 6127872 · DC 0737". */
export const lineWords = (l: Named) => [l.customerName || "—", lineRef(l)].filter(Boolean).join(" · ");

/** Selected lines an action would skip (already done) and those it would do. */
export interface SelectionSplit {
  done: LineView[];
  todo: LineView[];
}

/**
 * Schedule pickup and Add to an existing pickup (item 4): only the lines on no pickup go; the rest
 * are already scheduled. Every selected line on a pickup: the button is off ("Already scheduled").
 */
export const toSchedule = (ls: readonly LineView[]): SelectionSplit => ({
  done: ls.filter((l) => !!l.onPickup),
  todo: ls.filter((l) => !l.onPickup),
});

/** "TJ MAXX · WMS 37101 — pickup 10/23 · FedEx · 456457" */
export const scheduledText = (l: LineView) => `${lineWords(l)} — pickup ${pickupText(l.onPickup?.pickup ?? {})}`;

/** What Reschedule (item 2) sets on a pickup: every typed field of it, the carrier's code and source with it (item 8). */
export const RESCHEDULE_FIELDS = [
  "pickupDate",
  "appTime",
  "carrier",
  "carrierCode",
  "carrierSource",
  "door",
  "loadRef",
  "notes",
] as const;
export type RescheduleField = (typeof RESCHEDULE_FIELDS)[number];
export type RescheduleValues = Record<RescheduleField, string>;

/** Reschedule of the selected Shipping schedule rows: their pickups, and the dialog's starting values. */
export interface RescheduleStart {
  /** The pickups the selected rows are on, each once. */
  pickups: PickupRecord[];
  /** Some pickup has a line that is not selected: "Change the whole pickup" or "Move only these N lines". */
  partial: boolean;
  /** Each field as the pickups all have it; "" where they differ. */
  prefill: RescheduleValues;
  /** The fields that differ across the pickups: "Several values — leave empty to keep each". */
  several: RescheduleField[];
}

const valueOf = (p: PickupRecord, f: RescheduleField) => String(p[f] ?? "");

export function rescheduleStart(selected: readonly ScheduleRow[], all: readonly ScheduleRow[]): RescheduleStart {
  const pickups = [...new Map(selected.map((r) => [r.pickup.id, r.pickup])).values()];
  const chosen = new Set(selected.map((r) => r.row.id));
  const partial = all.some((r) => pickups.some((p) => p.id === r.pickup.id) && !chosen.has(r.row.id));
  const several = RESCHEDULE_FIELDS.filter((f) => new Set(pickups.map((p) => valueOf(p, f))).size > 1);
  const prefill = Object.fromEntries(
    RESCHEDULE_FIELDS.map((f) => [f, several.includes(f) ? "" : pickups[0] ? valueOf(pickups[0], f) : ""]),
  ) as RescheduleValues;
  return { pickups, partial, prefill, several };
}

/**
 * A whole pickup as Reschedule leaves it: the typed values, but a field that differed across the
 * pickups and was left empty keeps this pickup's own. Only what changes.
 */
export function rescheduleChanges(
  pickup: PickupRecord,
  typed: RescheduleValues,
  several: readonly RescheduleField[],
): Partial<RescheduleValues> {
  const out: Partial<RescheduleValues> = {};
  for (const f of RESCHEDULE_FIELDS) {
    const next = several.includes(f) && !typed[f].trim() ? valueOf(pickup, f) : typed[f].trim();
    if (next !== valueOf(pickup, f)) out[f] = next;
  }
  return out;
}

/**
 * Reschedule's checks, as scheduling's (`checkPickup`): each whole pickup with its new values and
 * every line on it; or, moving only some lines, the new pickup with those lines. Several pickups:
 * each message names its pickup.
 */
export function checkReschedule(
  start: Pick<RescheduleStart, "pickups" | "several">,
  typed: RescheduleValues,
  how: "whole" | "move",
  lines: { selected: readonly LineView[]; onPickup: (pickupId: string) => readonly LineView[] },
  today: string,
): Checked {
  if (how === "move")
    return checkPickup({ pickupDate: typed.pickupDate, loadRef: typed.loadRef }, lines.selected, today);
  const errors: string[] = [];
  const warnings: string[] = [];
  for (const p of start.pickups) {
    const next = { ...p, ...rescheduleChanges(p, typed, start.several) } as PickupRecord;
    const c = checkPickup(
      { pickupDate: valueOf(next, "pickupDate"), loadRef: valueOf(next, "loadRef") },
      lines.onPickup(p.id),
      today,
    );
    const tag = (m: string) => (start.pickups.length > 1 ? `Pickup ${pickupText(p)}: ${m}` : m);
    errors.push(...c.errors.map(tag));
    warnings.push(...c.warnings.map(tag));
  }
  return { errors, warnings };
}
